import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { hasClass } from '@opten/dom/has-class';
import { getOffset } from '@opten/dom/get-offset';
import { getWindowScrollTop } from '@opten/dom/get-window-scroll-top';
import { parseCssUnit } from '@opten/number/parse-css-unit';
import { getCss } from '@opten/dom/get-css';

import { animateCss } from '../helpers';
import { CrossSellingBox } from '../box';

export class CrossSellingOptions {
	offsetTop?: number;
	gutterTop?: number;
	gutterBottom?: number;
	selectors?: {
		box: string;
		body: string;
		toggle: string;
	};
	classes?: {
		fixed: string;
		computed: string;
		scrolled: string;
		expanded: string;
	};
	breakpoints?: {
		maxWidth: number;
	};
}

export class CrossSellingData {
	container: {
		top: number;
		left: number;
		width: number;
		parentHeight: number;
		isFixed: boolean;
	};
	scrollTop: number;
	scrapValue: number;
}

export class CrossSelling {
	private _data: CrossSellingData;
	private _parentElement: HTMLElement;
	private _scrapElement: HTMLElement;
	private _cloneElement: HTMLElement;

	private _options: CrossSellingOptions = {
		offsetTop: 0,
		gutterTop: 0,
		gutterBottom: 0,
		selectors: {
			box: 'js-cross-selling-box',
			body: 'js-cross-selling-box-body',
			toggle: 'js-cross-selling-toggle',
		},
		classes: {
			fixed: 'is-fixed',
			computed: 'is-absolute',
			scrolled: 'is-scrolled',
			expanded: 'is-expanded',
		},
		breakpoints: {
			maxWidth: undefined,
		},
	};

	private _boxes: CrossSellingBox[];

	constructor(private _element: HTMLElement, options?: CrossSellingOptions) {
		this._options = { ...this._options, ...options };

		this._parentElement = this._element.parentElement;
		this._scrapElement = this._element.parentElement;
		this._cloneElement = this._element.cloneNode(true) as HTMLElement;

		this._init();
	}

	private _init() {
		this._setData();
		this._boxes = this._getBoxes();

		this._handleBreakpointChange();
		window.addEventListener('resize', () => this._handleBreakpointChange());
	}

	private _handleBreakpointChange() {
		// todo: something like isMediaBreakpointDown()?
		if (
			this._options.breakpoints.maxWidth &&
			window.innerWidth < this._options.breakpoints.maxWidth
		) {
			this._disableListeners();
			this._destroy();
		} else {
			this._setDesktopListeners();
			this._handleDesktopScroll();
		}
	}

	private _getBoxes(element: HTMLElement = this._element): CrossSellingBox[] {
		const boxes = [];

		// We cannot use map because we have to access the one before
		// to calculate some stuff...
		Array.from(
			element.getElementsByClassName(this._options.selectors.box)
		).forEach((boxElement: HTMLElement, index) => {
			const box = new CrossSellingBox(
				boxElement,
				this._options.selectors.body,
				this._options.selectors.toggle,
				this._options.gutterTop,
				index,
				index === 0 ? undefined : boxes[index - 1]
			);

			boxes.push(box);
		});

		return boxes;
	}

	private _setData(element: HTMLElement = this._element) {
		// todo: Somehow weird? When the user resizes (bigger) then the left is slow
		// but if the change the offset.top() to e.g. lement.getBoundingClientRect().top
		// it's working, but this makes no sense????
		const offset = getOffset(element);

		this._data = {
			container: {
				top: offset.top() - this._options.gutterTop,
				left: Math.round(offset.left()),
				width: Math.round(element.offsetWidth),
				parentHeight: Math.round(
					this._parentElement.offsetHeight - this._options.gutterTop
				),
				isFixed: false,
			},
			scrollTop: 0,
			scrapValue: 0,
		};
	}

	private _setDesktopListeners() {
		this._handleResizeChange();
		window.addEventListener('scroll', this._handleDesktopScroll);
		window.addEventListener('resize', this._handleResizeChange);

		this._boxes.forEach((box) => {
			box.toggleElement.addEventListener('click', (event) =>
				this._handleCollapsibleLink(event, box.data.index)
			);
		});
	}

	private _disableListeners() {
		window.removeEventListener('scroll', this._handleDesktopScroll);
		window.removeEventListener('resize', this._handleResizeChange);
		// todo: Remove click events?
	}

	private _handleDesktopScroll = () => {
		this._data.scrollTop = getWindowScrollTop();

		this._update();
	};

	private _handleResizeChange = () => {
		const cloneElement = this._cloneElement;
		const paddingLeft = parseCssUnit(
			getCss(this._parentElement, 'padding-left')
		);
		const paddingRight = parseCssUnit(
			getCss(this._parentElement, 'padding-right')
		);
		const paddingFull = paddingLeft + paddingRight;

		cloneElement.style.position = 'absolute';
		cloneElement.style.top = '0px';
		cloneElement.style.left = `${paddingLeft}px`;
		cloneElement.style.visibility = 'hidden';
		cloneElement.style.width = `calc(100% - ${paddingFull}px)`;

		this._parentElement.appendChild(cloneElement);
		this._parentElement.style.minHeight = null;
		this._parentElement.style.position = 'relative';

		this._setData(cloneElement);

		// update data
		this._getBoxes(cloneElement).forEach((box, index) => {
			this._boxes[index].data = box.data;
		});

		this._parentElement.removeChild(cloneElement);
		this._parentElement.style.position = null;

		// We call this function instead of just this._update();
		// because the scrollTop might changed due to resizing ;-)
		this._handleDesktopScroll();
	};

	private _update() {
		this._boxes.forEach((box) => this._updateBox(box));
		this._checkScrapValue();
		this._updateContainerState();
	}

	private _updateBox(box: CrossSellingBox) {
		this._updateBoxHeight(box);
		this._updateBoxTop(box);
	}

	private _updateBoxHeight(box: CrossSellingBox) {
		if (
			this._data.scrollTop >
			box.data.stickPosition + box.data.maxTopMarginMove
		) {
			box.data.body.currentHeight = Math.max(
				0,
				Math.round(
					box.data.body.height +
						box.data.stickPosition +
						box.data.maxTopMarginMove -
						this._data.scrollTop
				)
			);

			box.data.currentHeight = Math.round(
				box.data.height - box.data.body.height + box.data.body.currentHeight
			);

			requestAnimationFrame(() =>
				addClass(box.element, this._options.classes.scrolled)
			);

			if (!box.data.state.isExpanded) {
				this._updateAria(box, false);
			}

			box.data.state.isFixed = false;
		} else {
			box.data.body.currentHeight = Math.round(box.data.body.height);

			box.data.currentHeight = Math.round(
				box.data.height - box.data.body.height + box.data.body.currentHeight
			);

			requestAnimationFrame(() => {
				removeClass(box.element, this._options.classes.scrolled);
				removeClass(box.element, this._options.classes.expanded);
			});

			box.data.state.isExpanded = false;
			box.data.state.isFixed = true;
			this._updateAria(box, true);
		}

		if (!box.data.state.isExpanded) {
			requestAnimationFrame(() =>
				box.bodyElement.setAttribute(
					'style',
					`height: ${box.data.body.currentHeight}px`
				)
			);

			if (!hasClass(box.element, this._options.classes.computed)) {
				requestAnimationFrame(() =>
					addClass(box.element, this._options.classes.computed)
				);
			}
		}
	}

	private _updateBoxTop(box: CrossSellingBox) {
		let top = 0;
		if (this._data.scrollTop > box.data.stickPosition && box.data.index > 0) {
			top = Math.min(
				Math.round(this._data.scrollTop - box.data.stickPosition),
				box.data.maxTopMarginMove
			);
		}

		requestAnimationFrame(() => (box.element.style.marginTop = `-${top}px`));
	}

	private _handleCollapsibleLink(event: Event, index: number) {
		event.preventDefault();

		if (this._boxes[index].data.state.isFixed) {
			return;
		}

		this._toggleBox(this._boxes[index]);
	}

	private _toggleBox(box: CrossSellingBox) {
		if (box.data.state.isExpanded) {
			this._collapseBox(box);
		} else {
			this._expandBox(box);
		}
	}

	private _collapseBox(box: CrossSellingBox) {
		if (box.data.state.isExpanded) {
			this._updateScrap(box, false);
		}

		animateCss(
			box.bodyElement,
			'height',
			box.bodyElement.offsetHeight,
			-box.bodyElement.offsetHeight,
			300,
			() => {
				removeClass(box.element, this._options.classes.expanded);
				this._updateAria(box, false);
			}
		);

		box.data.state.isExpanded = false;
	}

	private _expandBox(box: CrossSellingBox) {
		this._collapseAllOpenedBoxes();
		this._updateScrap(box, true);

		setTimeout(() => {
			animateCss(
				box.bodyElement,
				'height',
				box.bodyElement.offsetHeight,
				box.data.body.height - box.bodyElement.offsetHeight,
				300,
				() => {
					addClass(box.element, this._options.classes.expanded);
					this._updateAria(box, true);
				}
			);
		}, 100);

		box.data.state.isExpanded = true;
	}

	private _collapseAllOpenedBoxes() {
		this._boxes.forEach((box) => {
			if (box.data.state.isExpanded) {
				this._collapseBox(box);
			}
		});
	}

	private _updateAria(box: CrossSellingBox, expanded: boolean) {
		if (!expanded) {
			box.bodyElement.setAttribute('aria-hidden', 'true');
		} else {
			box.bodyElement.setAttribute('aria-hidden', 'false');
		}
	}

	private _getAllBoxesHeight() {
		let height = 0;

		this._boxes.forEach((box) => {
			box.data.state.isExpanded
				? (height += box.data.height)
				: (height += box.data.currentHeight);
		});

		return height;
	}

	private _updateContainerState() {
		if (!this._data.container.isFixed) {
			this._parentElement.style.minHeight = `${
				this._data.container.parentHeight + this._options.gutterTop
			}px`;
		}

		if (
			this._data.scrollTop >
			this._data.container.top -
				this._options.gutterTop -
				this._options.offsetTop
		) {
			if (!this._data.container.isFixed) {
				this._data.container.isFixed = true;
				requestAnimationFrame(() => {
					addClass(this._element, this._options.classes.fixed);

					this._element.setAttribute(
						'style',
						`top: ${this._options.gutterTop}px;
                         left: ${this._data.container.left}px;
                         width: ${this._data.container.width}px;
                        `
					);
				});
			}

			if (this._data.scrapValue < 0) {
				requestAnimationFrame(
					() =>
						(this._element.style.top = `${
							this._data.scrapValue + this._options.gutterTop
						}px`)
				);
			} else {
				requestAnimationFrame(
					() => (this._element.style.top = `${this._options.gutterTop}px`)
				);
			}
		} else {
			if (this._data.container.isFixed) {
				this._data.container.isFixed = false;

				requestAnimationFrame(() => {
					removeClass(this._element, this._options.classes.fixed);
					this._element.removeAttribute('style');
				});
			}
		}
	}

	private _checkScrapValue() {
		const offset = getOffset(this._scrapElement);

		const scrapValue =
			offset.top() +
			this._scrapElement.offsetHeight -
			this._options.gutterTop -
			this._options.gutterBottom -
			this._getAllBoxesHeight();

		this._data.scrapValue = Math.round(scrapValue - this._data.scrollTop);
	}

	private _updateScrap(box: CrossSellingBox, expanded: boolean) {
		let targetHeight = 0;

		const difference = box.data.body.height - box.data.body.currentHeight;

		if (this._data.scrapValue <= difference) {
			if (expanded) {
				targetHeight =
					this._data.container.parentHeight +
					this._options.gutterTop +
					difference -
					this._data.scrapValue;
				if (this._data.scrapValue < 0) {
					targetHeight += this._data.scrapValue;
				}
			} else {
				targetHeight =
					this._data.container.parentHeight + this._options.gutterTop;
			}

			// todo: buggy, if we are at the bottom there's somehow a space (open one and then open the one above)?!
			// todo: buggy, if we have one open and scroll top, the min-height is still the same
			animateCss(
				this._scrapElement,
				'min-height',
				this._scrapElement.offsetHeight,
				targetHeight > this._scrapElement.offsetHeight
					? targetHeight - this._scrapElement.offsetHeight
					: -(this._scrapElement.offsetHeight - targetHeight),
				300,
				() => this._update()
			);
		}
	}

	private _destroy() {
		requestAnimationFrame(() => {
			removeClass(this._element, this._options.classes.fixed);

			this._element.removeAttribute('style');
			this._parentElement.style.minHeight = null;

			this._boxes.forEach((box) => {
				removeClass(box.element, this._options.classes.computed);
				removeClass(box.element, this._options.classes.scrolled);
				removeClass(box.element, this._options.classes.expanded);
				box.element.style.marginTop = null;
				box.bodyElement.style.height = null;
				box.data.state.isExpanded = false;
				box.data.state.isFixed = false;
			});
		});
	}
}

export function crossSelling(
	element: HTMLElement,
	options?: CrossSellingOptions
): CrossSelling {
	return new CrossSelling(element, options);
}
